/*
 * Copyright (c) 2021. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import wizardStyle from "../wizardStyle";
import FormFader from "../Common/Fader/FormFader";
import Hidden from "@mui/material/Hidden";
import Grid from "@mui/material/Grid";
import MultipleFileUpload from "../Common/FileUpload/MultipleFileUpload";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import { Box, Checkbox } from "@mui/material";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import CaseSpecificDocumentUploadSection from "./CaseSpecificDocumentUploadSection";
import { filterMediaObjectsByFieldname } from "../../../services/mediaObjects";
import FileUploadSection from "../Common/FileUploadSection/FileUploadSection";
import { useForm } from "../../../provider/Form/FormProvider";
import { ContractDocumentsProps } from "../../../types/ContractDocuments";

export default function RentalContractDocuments({
  product,
  submitProps,
  isContractDocumentRequired,
  contractUploadBlocked,
  setContractUploadBlocked,
  refreshCase,
  showContractDocumentRequiredWarning,
  refreshMediaObjectCollection,
  mediaObjectCollection,
  updateAction,
  submitIsDisabled,
  setCaseSpecificSubmitDisabled,
  mediaObjectCombinationRequest,
}: ContractDocumentsProps) {
  const { values, handleSubmit, handleChange } = useForm();

  let headline = isContractDocumentRequired
    ? "Bitte lassen Sie uns hier alle Unterlagen zu Ihrem Mietvertrag und Ihrem Anliegen vollständig zukommen, damit wir Ihren Fall schnellstmöglich bearbeiten können."
    : "Sie können hier gerne Unterlagen zu Ihrem Fall hochladen. Wenn Sie keine Unterlagen hochladen möchten, klicken Sie unten einfach auf “WEITER”.";

  const opposingParty = product.contractPartyType === "landlord" ? "Mieter" : "Vermieter";

  return (
    <FormFader>
      <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
        <Hidden smUp>
          <Typography variant="h4" sx={wizardStyle.headline}>
            Unterlagen
          </Typography>
        </Hidden>
        <Grid container justifyContent={"flex-start"} alignItems={"baseline"}>
          <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
            <FormLegend>{headline}</FormLegend>
          </Grid>
          <Grid item xs={12}>
            <FileUploadSection
              productEntity={product}
              mediaObjectCombinationRequest={mediaObjectCombinationRequest}
              temporaryMediaObjectsFieldname={"contractDocumentFiles"}
              setIsBlocked={setContractUploadBlocked}
              isBlocked={contractUploadBlocked}
              product={product.name}
              productEntityId={product.id}
              updateAction={updateAction}
              fieldName={"contractFiles"}
              postSubmitCallback={refreshCase}
              description={"Mietvertrag"}
              headline={"Mietvertrag"}
              helperText={
                isContractDocumentRequired
                  ? "Bitte laden Sie hier Ihren Mietvertrag in gut lesbar und inkl. aller Seiten und Anhänge hoch."
                  : "Hier können Sie Ihren Mietvertrag hochladen. Achten Sie darauf, dass der Vertrag möglichst vollständig ist."
              }
            />
          </Grid>
          {showContractDocumentRequiredWarning && (
            <>
              <Grid item xs={12} sx={wizardStyle.hintHeader}>
                <strong>Falls Ihnen (gerade) keine Unterlagen vorliegen:</strong>
              </Grid>
              <Grid item xs={2} sm={1} sx={wizardStyle.checkbox}>
                <Checkbox name={"clientHasNoContractDocument"} checked={!!values.clientHasNoContractDocument} onChange={handleChange} />
              </Grid>
              <Grid item xs={10} sm={11} sx={submitIsDisabled ? wizardStyle.warningText : undefined}>
                Ich versichere, dass mir kein Mietvertrag vorliegt. Mir ist bewusst, dass daher eine Prüfung und Bearbeitung meines Falls gegebenenfalls nicht
                möglich ist.
              </Grid>
            </>
          )}
          <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
            <Typography variant="h5">{`Schriftverkehr mit dem ${opposingParty}`}</Typography>
            <Typography>
              Hatten Sie zu Ihrem aktuellen Anliegen bereits Schriftverkehr mit Ihrem {opposingParty}, z.B. E-Mails, Briefe, Whatsapp-Verläufe etc.? Dann
              fotografieren Sie diese bitte ab oder machen Sie einfach einen Screenshot davon und laden diesen dann entsprechend hier hoch.
            </Typography>
            <MultipleFileUpload
              product={product}
              fieldname={"contractPartnerCorrespondence"}
              description={"Schriftverkehr mit Vermieter"}
              mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, "contractPartnerCorrespondence")}
              fetchMediaObjects={refreshMediaObjectCollection}
            />
          </Grid>
          <CaseSpecificDocumentUploadSection
            fetchMediaObjectCollection={refreshMediaObjectCollection}
            mediaObjectCollection={mediaObjectCollection}
            product={product}
            setCaseSpecificSubmitDisabled={setCaseSpecificSubmitDisabled}
            refreshCase={refreshCase}
          />
          <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
            <Typography variant="h5">Weitere Unterlagen</Typography>
            <p>Haben Sie noch weitere Unterlagen, die zum Verständnis Ihres Falls wichtig sind? Diese können Sie hier hochladen.</p>
            <MultipleFileUpload
              product={product}
              fieldname={"applicationDocument"}
              description={"Weitere Unterlagen"}
              mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, "applicationDocument")}
              fetchMediaObjects={refreshMediaObjectCollection}
            />
          </Grid>
        </Grid>
        <br />
        <br />
        <FormSubmit {...submitProps} disabled={submitIsDisabled} />
      </Box>
    </FormFader>
  );
}
